/**
 * Server-corrected clock. Card timers must agree across screens even when
 * device clocks are skewed, so we estimate (serverTime - localTime) and apply
 * it via serverNow().
 */
let offsetMs = 0;

export const getServerOffsetMs = (): number => offsetMs;

export const serverNow = (): number => Date.now() + offsetMs;

export const resetServerClock = (): void => {
  offsetMs = 0;
};

/**
 * Samples the server clock and keeps the offset from the lowest-RTT sample
 * (NTP-style midpoint estimate). Never throws; failed/invalid samples are
 * ignored and the previous offset is kept if no sample is valid.
 */
export const syncServerClock = async (
  fetchServerTime: () => Promise<number>,
  samples = 3
): Promise<void> => {
  let best: { rtt: number; offset: number } | undefined;
  for (let i = 0; i < samples; i++) {
    try {
      const t0 = Date.now();
      const serverTime = await fetchServerTime();
      const t1 = Date.now();
      if (typeof serverTime !== "number" || !Number.isFinite(serverTime)) {
        continue;
      }
      const rtt = t1 - t0;
      // A negative RTT means the wall clock stepped mid-call; discard it.
      if (rtt < 0) continue;
      if (!best || rtt < best.rtt) {
        best = { rtt, offset: serverTime - (t0 + t1) / 2 };
      }
    } catch {
      // ignore failed sample
    }
  }
  if (best) offsetMs = best.offset;
};
